/* ------------------------------------------------------------------------- *
 * A conta do calendário, sem React e sem fuso
 *
 * Tudo aqui trabalha com a data de negócio (`AAAA-MM-DD`) e com `Date.UTC`,
 * nunca com `new Date(ano, mês, dia)`: o construtor local lê o fuso da máquina,
 * e numa máquina com horário de verão a meia-noite de um dia de transição não
 * existe — o dia "some" da grade. Em UTC todo dia tem 24 horas.
 *
 * O nome do mês e do dia vêm do `Intl` com `pt-BR` ESCRITO, e não da língua do
 * navegador — é exatamente o defeito do controle nativo que o `DateField`
 * existe para fechar.
 * ------------------------------------------------------------------------- */

export interface DateParts {
  year: number;
  /** 1 a 12. */
  month: number;
  day: number;
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `AAAA-MM-DD` → partes, ou `null` se não for ISO ou não existir. */
export function partsOf(iso: string): DateParts | null {
  const m = ISO.exec(iso.trim());
  if (m === null) return null;
  const parts = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  const d = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  return d.getUTCMonth() === parts.month - 1 && d.getUTCDate() === parts.day
    ? parts
    : null;
}

function isoOfUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Partes → `AAAA-MM-DD`. O que transborda (dia 32, mês 13) rola para a frente. */
export function isoOf(year: number, month: number, day: number): string {
  return isoOfUtc(new Date(Date.UTC(year, month - 1, day)));
}

/** Quantos dias tem o mês. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** `n` dias depois (ou antes, com `n` negativo). */
export function addDays(iso: string, n: number): string {
  const p = partsOf(iso);
  if (p === null) return iso;
  return isoOf(p.year, p.month, p.day + n);
}

/**
 * `n` meses depois, com o dia SEGURO no fim do mês.
 *
 * 31/01 mais um mês é 28/02 (ou 29), e não 03/03 — que é o que a rolagem do
 * `Date.UTC` daria. No calendário isso é o PageDown: quem está no dia 31 e
 * avança um mês espera cair no último dia do mês seguinte, não pular dois.
 */
export function addMonths(iso: string, n: number): string {
  const p = partsOf(iso);
  if (p === null) return iso;
  const first = new Date(Date.UTC(p.year, p.month - 1 + n, 1));
  const year = first.getUTCFullYear();
  const month = first.getUTCMonth() + 1;
  return isoOf(year, month, Math.min(p.day, daysInMonth(year, month)));
}

/** 0 é domingo — a semana daqui começa no domingo, como a folhinha. */
export function weekdayOf(iso: string): number {
  const p = partsOf(iso);
  if (p === null) return 0;
  return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
}

/**
 * A grade do mês: SEIS semanas de sete dias, de domingo a sábado.
 *
 * Sempre seis, mesmo quando o mês cabe em cinco (ou em quatro, fevereiro de
 * 2026). Com a altura variável o calendário crescia e encolhia a cada clique
 * de "próximo mês", e o botão que se acabou de clicar pulava de lugar embaixo
 * do ponteiro.
 */
export function monthGrid(year: number, month: number): string[][] {
  const first = isoOf(year, month, 1);
  const start = addDays(first, -weekdayOf(first));
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)),
  );
}

const MONTH = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});

const FULL = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** "setembro de 2026" — o título do mês na grade. */
export function monthTitle(year: number, month: number): string {
  return MONTH.format(new Date(Date.UTC(year, month - 1, 1)));
}

/** "sexta-feira, 25 de setembro de 2026" — o nome acessível de cada dia. */
export function dayLabel(iso: string): string {
  const p = partsOf(iso);
  if (p === null) return iso;
  return FULL.format(new Date(Date.UTC(p.year, p.month - 1, p.day)));
}

/** O cabeçalho da grade: a letra que se vê e o nome que se lê. */
export const WEEKDAYS: readonly { short: string; long: string }[] = [
  { short: 'D', long: 'domingo' },
  { short: 'S', long: 'segunda-feira' },
  { short: 'T', long: 'terça-feira' },
  { short: 'Q', long: 'quarta-feira' },
  { short: 'Q', long: 'quinta-feira' },
  { short: 'S', long: 'sexta-feira' },
  { short: 'S', long: 'sábado' },
];
